import { FC, useEffect, useId, useMemo, useRef, useState } from "react";
import { Info } from "lucide-react";
import ROUTES from "@/data/Routes";
import { ActionLink } from "@/components/UI/Buttons";
import useMediaQuery from "@/custom_hooks/mediaQuery";

interface MembershipPricingCardProps {
  cardImage: string;
  backgroundVideoSrc?: string;
  cardTitle: string;
  price: string;
  onCompare?: () => void;
  isDark: boolean;
  bgClass: string;
  textClass: string;
}

const MembershipPricingCard: FC<MembershipPricingCardProps> = ({
  cardImage,
  backgroundVideoSrc,
  cardTitle,
  price,
  onCompare,
  isDark,
  bgClass,
  textClass,
}) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const tooltipRootRef = useRef<HTMLSpanElement | null>(null);
  const tooltipId = useId();
  const isMobile = useMediaQuery(767);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isVideoVisible, setIsVideoVisible] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  useEffect(() => {
    if (!isTooltipOpen) return;

    const onPointerDown = (event: PointerEvent) => {
      const root = tooltipRootRef.current;
      if (!root) return;
      if (event.target instanceof Node && root.contains(event.target)) return;
      setIsTooltipOpen(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsTooltipOpen(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isTooltipOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQueryList = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    );
    if (!mediaQueryList) return;

    const onChange = () => setPrefersReducedMotion(mediaQueryList.matches);
    onChange();

    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener("change", onChange);
      return () => mediaQueryList.removeEventListener("change", onChange);
    }

    mediaQueryList.addListener(onChange);
    return () => mediaQueryList.removeListener(onChange);
  }, []);

  const shouldShowBackgroundVideo =
    Boolean(backgroundVideoSrc) && isMobile && !prefersReducedMotion;

  const videoBlendModeClass = useMemo(() => {
    return isDark ? "mix-blend-screen" : "mix-blend-multiply";
  }, [isDark]);

  useEffect(() => {
    if (!shouldShowBackgroundVideo) return;
    if (typeof window === "undefined") return;

    const node = cardRef.current;
    const video = videoRef.current;
    if (!node || !video) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        if (entry.isIntersecting) {
          video
            .play()
            .then(() => setIsVideoVisible(true))
            .catch(() => setIsVideoVisible(false));
        } else {
          video.pause();
          video.currentTime = 0;
          setIsVideoVisible(false);
        }
      },
      { threshold: 0.35 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [shouldShowBackgroundVideo]);

  return (
    <div
      ref={cardRef}
      className={`${bgClass} relative isolate flex w-full shrink-0 flex-col items-center gap-8 overflow-hidden rounded-[26px] p-6 md:p-[60px] lg:w-[443px]`}
    >
      {shouldShowBackgroundVideo ? (
        <div
          className="pointer-events-none absolute inset-0 z-0 overflow-hidden md:hidden"
          style={{
            WebkitMaskImage:
              "radial-gradient(circle at top right, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 72%, rgba(0,0,0,0) 100%)",
            maskImage:
              "radial-gradient(circle at top right, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 72%, rgba(0,0,0,0) 100%)",
          }}
        >
          <video
            ref={videoRef}
            className={`h-full w-full object-cover opacity-0 transition-opacity duration-300 ${videoBlendModeClass} ${
              isVideoVisible ? "opacity-70" : "opacity-0"
            }`}
            muted
            playsInline
            loop
            preload="metadata"
            aria-hidden="true"
          >
            <source src={backgroundVideoSrc} type="video/mp4" />
          </video>
        </div>
      ) : null}

      <div className="relative z-10 flex h-auto w-[180px] flex-col items-center md:w-[235px]">
        <img
          src={cardImage}
          alt={cardTitle}
          className="w-full object-contain"
        />
      </div>
      <div
        className={`relative z-10 flex w-full flex-col gap-[30px] ${textClass}`}
      >
        <h3 className="text-center text-[28px] font-normal md:text-[34px]">
          {cardTitle}
        </h3>
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-[6px]">
            <span className="text-[16px] md:text-[18px]">
              Pirmų metų narystė
            </span>
            <span
              ref={tooltipRootRef}
              className="group relative inline-flex items-center justify-center"
            >
              <button
                type="button"
                aria-label="Narystės kainos informacija"
                aria-expanded={isTooltipOpen}
                aria-controls={tooltipId}
                onClick={() => setIsTooltipOpen((open) => !open)}
                className="cursor-help p-1 opacity-50 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
              >
                <Info size={16} />
              </button>
              <span
                id={tooltipId}
                role="tooltip"
                className={`pointer-events-none invisible absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 ${
                  isTooltipOpen ? "visible opacity-100" : ""
                }`}
              >
                <span
                  className={`block w-[200px] rounded-md px-3 py-2 text-center text-[13px] leading-tight font-normal whitespace-normal shadow-xl ${
                    isDark ? "text-dark bg-white" : "bg-dark text-white"
                  }`}
                >
                  Į pirmų metų narystę įtrauktas išsamus pradinis ištyrimas,
                  įskaitant genetikos analizę, todėl nuo antrų metų narystės
                  kaina – iki €500
                </span>
              </span>
            </span>
          </div>
          <span className="text-[16px] font-medium md:text-[18px]">
            {price}
          </span>
        </div>
        <div className="mt-4 flex w-full flex-col items-center gap-[6px]">
          <ActionLink
            variant={isDark ? "light" : "dark"}
            to={ROUTES.CONSULTATION}
            showIcon={false}
            hoverGradientLight
            onHover
            className="w-full justify-center px-4.5 py-3.5 text-[14px] md:w-full md:flex-none md:px-[20px] md:py-[20px] md:text-base"
          >
            Registruotis konsultacijai
          </ActionLink>
          <ActionLink
            variant={isDark ? "dark" : "light"}
            to="#"
            onClick={(e) => {
              e.preventDefault();
              onCompare?.();
            }}
            showIcon={false}
            onHover
            hoverGradientLight
            className="w-full justify-center px-4.5 py-3.5 text-[14px] md:flex-none md:px-[20px] md:py-[20px] md:text-base"
          >
            Palyginti narystes
          </ActionLink>
          <p className={`mt-2 text-center text-[12px] leading-[1.4] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            Siekiant užtikrinti maksimalų dėmesį ir priežiūros kokybę, narystės
            vietų skaičius ribojamas
          </p>
        </div>
      </div>
    </div>
  );
};

export default MembershipPricingCard;
